import React, { useCallback, useEffect, useState } from "react";
import { FaCrown } from "react-icons/fa6";
import moment from "moment";
import { useDispatch } from "react-redux";
import ModalBasic from "../../components/ModalBasic.jsx";
import {
  PrimaryButton,
  SecondaryButton,
  SecondaryDangerButton,
} from "../../components/Buttons.jsx";
import { addToast } from "../../store/app/action.js";
import {
  getPendingCustomProductComplianceReviews,
  decideCustomProductComplianceReview,
} from "../../api/admin.js";
import { useHotkeys } from "react-hotkeys-hook";

const SCREENING_ROWS = [
  ["is_vape", "Vape product"],
  ["is_flavored_vape", "Flavored vape product"],
  ["contains_hemp_derived_cannabinoid", "Hemp-derived cannabinoid"],
  ["is_weapon", "Weapon or weapon-related"],
  ["is_illegal_substance", "Illegal substance concern"],
  ["is_prostitution_service", "Prostitution / escort concern"],
];

function activeScreeningLabels(screening) {
  if (!screening || typeof screening !== "object") return [];
  return SCREENING_ROWS.filter(([key]) => screening[key]).map(([, label]) => label);
}

function ReviewCustomProducts() {
  const dispatch = useDispatch();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { reviews: list } = await getPendingCustomProductComplianceReviews();
      setReviews(list || []);
    } catch (e) {
      dispatch(
        addToast({
          type: "error",
          message:
            e.response?.data?.error ||
            "Could not load pending custom product reviews",
          timer: 6,
        })
      );
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const closeModal = () => setSelected(null);

  const submitDecision = async (decision) => {
    if (!selected?._id) return;
    setDecisionLoading(decision);
    try {
      await decideCustomProductComplianceReview(selected._id, decision);
      dispatch(
        addToast({
          type: "success",
          message:
            decision === "approve"
              ? "Product approved and published per merchant settings."
              : "Product declined and disabled.",
          timer: 5,
        })
      );
      closeModal();
      await load();
    } catch (e) {
      dispatch(
        addToast({
          type: "error",
          message:
            e.response?.data?.error || "Could not update review — try again",
          timer: 6,
        })
      );
    } finally {
      setDecisionLoading(null);
    }
  };

  const product = selected?.custom_product;
  const merchant = selected?.merchant_id;
  const snapshot = selected?.request_snapshot || {};
  const screening = selected?.screening || {};

  // Keyboard shortcuts for approve / decline when modal is open
  useHotkeys(
    "ctrl+a,ctrl+A,meta+a,meta+A",
    (event) => {
      if (!selected || decisionLoading) return;
      const target = event.target;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }
      event.preventDefault();
      submitDecision("approve");
    },
    { enableOnFormTags: ["INPUT", "TEXTAREA", "SELECT"] },
    [selected, decisionLoading]
  );

  useHotkeys(
    "ctrl+d,ctrl+D,meta+d,meta+D",
    (event) => {
      if (!selected || decisionLoading) return;
      const target = event.target;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }
      event.preventDefault();
      submitDecision("reject");
    },
    { enableOnFormTags: ["INPUT", "TEXTAREA", "SELECT"] },
    [selected, decisionLoading]
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      <div className="sm:flex sm:justify-between sm:items-center mb-8">
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold flex items-center gap-2">
            Review custom products <FaCrown className="text-amber-400 shrink-0" />
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
            Listings flagged by automated compliance screening stay in draft until
            you approve them. Approving applies the merchant&apos;s chosen stores,
            pricing, and publish intent.
          </p>
        </div>
        <SecondaryButton type="button" onClick={load} disabled={loading}>
          Refresh
        </SecondaryButton>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-theme-surface-accent shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500 dark:text-slate-400 text-sm">
            Loading pending reviews…
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-slate-600 dark:text-slate-300 font-medium">
              No products awaiting review
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              New submissions that need a human check will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-auto w-full dark:text-slate-300 min-w-[720px]">
              <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold w-16">
                    &nbsp;
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">Product</th>
                  <th className="px-4 py-3 text-left font-semibold">Merchant</th>
                  <th className="px-4 py-3 text-left font-semibold">Flags</th>
                  <th className="px-4 py-3 text-left font-semibold">Submitted</th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                {reviews.map((row) => {
                  const p = row.custom_product;
                  const m = row.merchant_id;
                  const labels = activeScreeningLabels(row.screening);
                  const img =
                    p?.image_large || p?.image || null;
                  return (
                    <tr
                      key={row._id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition-colors"
                      onClick={() => setSelected(row)}
                    >
                      <td className="px-4 py-3">
                        <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 overflow-hidden border border-slate-200 dark:border-slate-600 shrink-0">
                          {img ? (
                            <img
                              src={img}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400">
                              —
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800 dark:text-slate-100">
                          {p?.title || "—"}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 font-mono mt-0.5">
                          {row.sku_id}
                        </div>
                        {p?.volume_label && (
                          <div className="text-xs text-slate-500 mt-0.5">
                            {p.volume_label}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-700 dark:text-slate-200">
                          {m?.name || "—"}
                        </div>
                        {m?.easy_id && (
                          <div className="text-xs text-slate-500 font-mono">
                            {m.easy_id}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {labels.length === 0 ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : (
                            labels.map((l) => (
                              <span
                                key={l}
                                className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
                              >
                                {l}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        {row.created
                          ? moment(row.created).format("MMM D, YYYY h:mm A")
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModalBasic
        id="review-custom-product-modal"
        title="Review custom product"
        modalOpen={!!selected}
        setModalOpen={(open) => {
          if (!open) closeModal();
        }}
        width="4xl"
        className="!max-h-[min(92vh,900px)]"
      >
        {selected && (
          <div className="space-y-6">
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-[220px] shrink-0">
                <div className="aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600">
                  {product?.image_large || product?.image ? (
                    <img
                      src={product.image_large || product.image}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">
                      No image
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-0 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                    {product?.title}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 font-mono mt-1">
                    {selected.sku_id}
                  </p>
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-900/30 px-3 py-2 border border-slate-100 dark:border-slate-700/80">
                    <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Company
                    </dt>
                    <dd className="mt-0.5 font-medium text-slate-800 dark:text-slate-100">
                      {product?.company || "—"}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-900/30 px-3 py-2 border border-slate-100 dark:border-slate-700/80">
                    <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Volume / size
                    </dt>
                    <dd className="mt-0.5 font-medium text-slate-800 dark:text-slate-100">
                      {product?.volume_label || "—"}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-900/30 px-3 py-2 border border-slate-100 dark:border-slate-700/80 sm:col-span-2">
                    <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Description
                    </dt>
                    <dd className="mt-0.5 text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
                      {product?.description || "—"}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-900/30 px-3 py-2 border border-slate-100 dark:border-slate-700/80">
                    <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      UPC
                    </dt>
                    <dd className="mt-0.5 font-mono text-slate-800 dark:text-slate-100">
                      {product?.upc_code || snapshot.upc_code || "—"}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="rounded-xl border border-indigo-200/80 dark:border-indigo-800/60 bg-indigo-50/50 dark:bg-indigo-950/20 px-4 py-3">
              <h4 className="text-sm font-semibold text-indigo-900 dark:text-indigo-100 mb-2">
                Merchant
              </h4>
              <p className="text-sm text-indigo-950 dark:text-indigo-50">
                <span className="font-medium">{merchant?.name || "—"}</span>
                {merchant?.easy_id && (
                  <span className="text-indigo-700 dark:text-indigo-300 font-mono ml-2">
                    ({merchant.easy_id})
                  </span>
                )}
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-600">
                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Screening result
                </h4>
              </div>
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {SCREENING_ROWS.map(([key, label]) => {
                  const on = !!screening[key];
                  return (
                    <li
                      key={key}
                      className="flex items-center justify-between px-4 py-2.5 text-sm"
                    >
                      <span className="text-slate-700 dark:text-slate-200">
                        {label}
                      </span>
                      <span
                        className={
                          on
                            ? "text-amber-700 dark:text-amber-300 font-semibold"
                            : "text-emerald-700 dark:text-emerald-400"
                        }
                      >
                        {on ? "Flagged" : "Clear"}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-600 px-4 py-3 space-y-2">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Merchant submit intent
              </h4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div>
                  <dt className="text-slate-500 dark:text-slate-400">Status</dt>
                  <dd className="font-medium capitalize text-slate-800 dark:text-slate-100">
                    {snapshot.intended_status || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500 dark:text-slate-400">Price</dt>
                  <dd className="font-medium text-slate-800 dark:text-slate-100">
                    {snapshot.price != null && snapshot.price !== ""
                      ? `$${Number(snapshot.price).toFixed(2)}`
                      : "—"}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-slate-500 dark:text-slate-400">
                    Store IDs (publish targets)
                  </dt>
                  <dd className="font-mono text-xs text-slate-700 dark:text-slate-200 break-all mt-0.5">
                    {Array.isArray(snapshot.store_ids) &&
                    snapshot.store_ids.length
                      ? snapshot.store_ids.join(", ")
                      : "—"}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              <SecondaryButton
                type="button"
                onClick={closeModal}
                disabled={!!decisionLoading}
              >
                Cancel
              </SecondaryButton>
              <SecondaryDangerButton
                type="button"
                onClick={() => submitDecision("reject")}
                loading={decisionLoading === "reject"}
                disabled={!!decisionLoading}
              >
                Decline
              </SecondaryDangerButton>
              <PrimaryButton
                type="button"
                onClick={() => submitDecision("approve")}
                loading={decisionLoading === "approve"}
                disabled={!!decisionLoading}
              >
                Approve &amp; publish
              </PrimaryButton>
            </div>
          </div>
        )}
      </ModalBasic>
    </div>
  );
}

export default ReviewCustomProducts;
